// SHEventSettings.js
import { useEffect, useMemo, useState } from "react";
import Switch from "../../../../../components/Switch/Switch";
import InputField from "../../../../../components/InputField/InputField";
import "./SHEventSettings.css";
import Services from "../../../../../services/Services";
import { useLanguage } from "../../../../../LanguageProvider";
import { useToaster } from "../../../../../ToasterProvider";

export default function SHEventSettings({ event, getEventDetails }) {
    const { getTranslation } = useLanguage();
    const { toastSuccess } = useToaster();

    const defaults = useMemo(
        () => ({
            showTicketsOnSale: false,
            warnOnLowTickets: false,
            warnThreshold: null,
            showParticipantCount: false,
        }),
        []
    );

    // event.settings -> state initialize
    const normalizeSettings = (raw) => {
        if (!raw) return { ...defaults };
        return {
            showTicketsOnSale: !!raw.showTicketsOnSale,
            warnOnLowTickets: !!raw.warnOnLowTickets,
            warnThreshold:
                raw.warnThreshold === 0
                    ? 0
                    : Number.isFinite(Number(raw?.warnThreshold))
                        ? Number(raw.warnThreshold)
                        : null,
            showParticipantCount: !!raw.showParticipantCount,
        };
    };

    const initial = useMemo(() => normalizeSettings(event?.settings), [event?.settings]);

    const [isOnSaleVisible, setIsOnSaleVisible] = useState(initial.showTicketsOnSale);
    const [warnLowTicketEnabled, setWarnLowTicketEnabled] = useState(initial.warnOnLowTickets);
    const [warnThreshold, setWarnThreshold] = useState(
        initial.warnThreshold === null || initial.warnThreshold === undefined
            ? ""
            : String(initial.warnThreshold)
    );
    const [showParticipantCount, setShowParticipantCount] = useState(initial.showParticipantCount);

    const [saving, setSaving] = useState(false);

    // event değişirse state’i yeniden kur
    useEffect(() => {
        const i = normalizeSettings(event?.settings);
        setIsOnSaleVisible(i.showTicketsOnSale);
        setWarnLowTicketEnabled(i.warnOnLowTickets);
        setWarnThreshold(i.warnThreshold === null || i.warnThreshold === undefined ? "" : String(i.warnThreshold));
        setShowParticipantCount(i.showParticipantCount);
    }, [event?.settings]);

    // Numeric input kontrolü
    const handleThresholdChange = (e) => {
        const v = e.target.value;
        if (v === "" || /^[0-9]+$/.test(v)) setWarnThreshold(v);
    };

    // UI -> settings objesi
    const buildSettings = () => ({
        showTicketsOnSale: !!isOnSaleVisible,
        warnOnLowTickets: !!warnLowTicketEnabled,
        warnThreshold:
            warnLowTicketEnabled
                ? (warnThreshold === "" ? null : Number.parseInt(warnThreshold, 10))
                : null,
        showParticipantCount: !!showParticipantCount,
    });

    // Dirty check
    const isDirty = useMemo(() => {
        const now = buildSettings();
        // initial.warnThreshold "" değil; normalize edilmişti, burada string'e çevirmiyoruz.
        const base = {
            showTicketsOnSale: initial.showTicketsOnSale,
            warnOnLowTickets: initial.warnOnLowTickets,
            warnThreshold: initial.warnThreshold,
            showParticipantCount: initial.showParticipantCount,
        };
        return JSON.stringify(now) !== JSON.stringify(base);
    }, [isOnSaleVisible, warnLowTicketEnabled, warnThreshold, showParticipantCount, initial]);

    const handleSave = async () => {
        if (!event?.channelID || !event?.eventID) {
            console.error("Missing channelID or eventID in event");
            return;
        }
        const token = localStorage.getItem("token");
        const payload = {
            channelID: event.channelID,
            eventID: event.eventID,
            hasTickets: event.hasTickets,
            settings: buildSettings(),
        };

        try {
            setSaving(true);
            let res = await Services.SocialHiveUpdateEvent(token, payload);
            if (res.data.status) {
                toastSuccess(getTranslation("successMessage"));
            }
            if (typeof getEventDetails === "function") {
                await getEventDetails();
            }
        } catch (err) {
            console.error("SocialHiveUpdateEvent error:", err);
        } finally {
            setSaving(false);
        }
    };

    // Warn switch toggle handler
    const handleWarnToggle = (val) => {
        if (!isOnSaleVisible && val) {
            // showTicketsOnSale kapalıyken warn açılmaya çalışılırsa engelle
            return;
        }
        setWarnLowTicketEnabled(val);
    };

    // Eğer showTicketsOnSale kapanırsa warn'ı otomatik kapat
    useEffect(() => {
        if (!isOnSaleVisible) {
            setWarnLowTicketEnabled(false);
        }
    }, [isOnSaleVisible]);

    return (
        <div className="sh-settings-main">
            <div className="sh-settings-content">
                {/* Genel açıklama */}
                <p className="sh-es-description">{getTranslation("configureEventSettings")}</p>

                <div className="sh-es-grid">
                    {/* 1) Show tickets on sale */}
                    <div className="sh-es-card">
                        <div className="sh-es-card-body">
                            <span className="sh-es-help">{getTranslation("displayTicketsOnSale")}</span>
                            <Switch
                                checked={isOnSaleVisible}
                                onChange={setIsOnSaleVisible}
                                labelLeft={getTranslation("showTicketsOnSale")}
                                size="sm"
                            />
                        </div>
                    </div>

                    {/* 2) Show participant count */}
                    <div className="sh-es-card">
                        <div className="sh-es-card-body">
                            <span className="sh-es-help">{getTranslation("displayTotalParticipants")}</span>
                            <Switch
                                checked={showParticipantCount}
                                onChange={setShowParticipantCount}
                                labelLeft={getTranslation("showParticipantCount")}
                                size="sm"
                            />
                        </div>
                    </div>

                    {/* 3) Warn when X tickets left */}
                    <div className="sh-es-card sh-es-card-span2">
                        <div className="sh-es-card-body">
                            <span className="sh-es-help">
                                {getTranslation("enableLowTicketWarning")}
                                {!isOnSaleVisible && (
                                    <em style={{ color: "red", marginLeft: 8 }}>
                                        ({getTranslation("requiresShowTicketsOnSale")})
                                    </em>
                                )}
                            </span>
                            <div className="sh-es-inline">
                                <Switch
                                    checked={warnLowTicketEnabled}
                                    onChange={handleWarnToggle}
                                    labelLeft={getTranslation("warnWhenXTicketsLeft")}
                                    size="sm"
                                />
                                <InputField
                                    type="text"
                                    placeholder={getTranslation("remainingThreshold")}
                                    value={warnThreshold}
                                    onChange={handleThresholdChange}
                                    style={{
                                        width: 180,
                                        opacity: warnLowTicketEnabled ? 1 : 0.6,
                                        pointerEvents: warnLowTicketEnabled ? "auto" : "none",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        className="sh-settings-action-button"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        title={!isDirty ? "No changes to save" : "Save settings"}
                    >
                        {saving ? getTranslation("saving") : getTranslation("save")}
                    </button>
                </div>
            </div>
        </div>
    );
}